import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  ShieldAlert,
  Wallet,
  UserPlus,
  Users,
  BadgeCheck,
  Banknote,
  Landmark,
  Sparkles,
  Activity,
  TrendingUp,
  Smartphone,
  Clock,
  CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { anyAdminExists, grantAdminByEmail } from "@/lib/admin.functions";
import { resolveWalletUnlockRequest } from "@/lib/wallet-unlock-requests.functions";
import { ClientReleasesFeed } from "@/components/admin/ClientReleasesFeed";
import { AdminNav } from "@/components/admin/AdminNav";
import { WithdrawalsAdminPanel } from "@/components/admin/WithdrawalsAdminPanel";
import { OverviewMetrics } from "@/components/admin/OverviewMetrics";
import { useCountUp } from "@/hooks/use-count-up";
import { getPayoutSchema } from "@/lib/payout-schemas";
import { cn } from "@/lib/utils";

function formatMethodLabel(method: string | null | undefined): string {
  if (!method) return "—";
  if (method === "baridimob") return "BaridiMob";
  if (method === "bank_transfer") return "Bank transfer";
  if (method.startsWith("usdt")) return "Legacy USDT (retired)";
  if (method.startsWith("btc")) return "Legacy BTC (retired)";
  return method;
}

function resolveDestination(
  method: string | null | undefined,
  country: string | null | undefined,
  details: Record<string, string> | null | undefined,
  fallback?: string | null,
): string {
  const d = details ?? {};
  if (method === "baridimob") return d.rip ?? d.account_number ?? fallback ?? "";
  if (method === "bank_transfer" && country) {
    try {
      const schema = getPayoutSchema(country);
      const v = d[schema.destinationKey];
      if (v) return v;
    } catch {
      // ignore, fall through
    }
    return d.iban ?? d.account_number ?? d.routing_number ?? fallback ?? "";
  }
  return fallback ?? "";
}

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Platform admin — Lumvio" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

interface AdminInvoice {
  id: string;
  amount: number;
  net_amount: number | null;
  stripe_fee_amount: number | null;
  description: string | null;
  status: string;
  paid_at: string | null;
  released_at: string | null;
  platform_reference: string | null;
  freelancer_id: string;
  projects: { title: string; client_email: string } | null;
  freelancer: { full_name: string | null; email: string } | null;
}

function AdminPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: isAdmin, isLoading: checkingRole } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: user!.id,
        _role: "admin",
      });
      if (error) throw error;
      return data as boolean;
    },
    enabled: !!user,
  });

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["admin-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          "id, amount, net_amount, stripe_fee_amount, description, status, paid_at, released_at, platform_reference, freelancer_id, projects(title, client_email)",
        )
        .in("status", ["paid_to_platform", "released_to_freelancer"])
        .order("paid_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as Omit<AdminInvoice, "freelancer">[];
      const ids = Array.from(new Set(rows.map((r) => r.freelancer_id)));
      let profilesById = new Map<string, AdminInvoice["freelancer"]>();
      if (ids.length) {
        const { data: profs, error: pErr } = await supabase.from("profiles").select("id, full_name, email").in("id", ids);
        if (pErr) throw pErr;
        profilesById = new Map(
          (profs ?? []).map((p) => [
            p.id,
            { full_name: p.full_name, email: p.email },
          ]),
        );
      }
      return rows.map((r) => ({ ...r, freelancer: profilesById.get(r.freelancer_id) ?? null })) as AdminInvoice[];
    },
    enabled: !!isAdmin,
  });

  const release = useMutation({
    mutationFn: async (id: string) => {
      const tx = window.prompt("Transaction hash / reference from your USDT wallet (optional):", "");
      const { error } = await supabase
        .from("invoices")
        .update({
          status: "released_to_freelancer",
          released_at: new Date().toISOString(),
          released_by: user!.id,
          release_tx_hash: tx || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Marked as released to freelancer");
      qc.invalidateQueries({ queryKey: ["admin-invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: adminExistsData, isLoading: checkingBootstrap } = useQuery({
    queryKey: ["any-admin-exists"],
    queryFn: () => anyAdminExists(),
  });
  const bootstrapMode = adminExistsData?.exists === false;

  if (checkingRole || checkingBootstrap) {
    return <div className="py-16 text-center text-muted-foreground">Loading…</div>;
  }

  if (!isAdmin && !bootstrapMode) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border bg-card p-8 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
        <h1 className="mt-3 text-lg font-semibold">Admin only</h1>
        <p className="mt-1 text-sm text-muted-foreground">This page is for platform payout administrators.</p>
      </div>
    );
  }

  const pending = invoices?.filter((i) => i.status === "paid_to_platform") ?? [];
  const released = invoices?.filter((i) => i.status === "released_to_freelancer") ?? [];

  return (
    <div className="space-y-8">
      {/* Executive header */}
      <section className="relative overflow-hidden rounded-2xl border bg-card/80 p-5 sm:p-6">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-50 blur-3xl"
          style={{ background: "radial-gradient(circle at center, hsl(var(--primary)/0.22), transparent 60%)" }}
        />
        <div className="relative flex flex-col gap-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
                <Sparkles className="h-3 w-3" /> Admin Console 2.0
              </div>
              <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Platform payouts</h1>
              <p className="max-w-xl text-sm text-muted-foreground">
                Live command center for escrow, releases and platform health — everything updates in real time.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              All systems operational
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <AdminNav />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </span>
          </div>
        </div>
      </section>

      <AdminOverview />
      <OverviewMetrics />
      <GrantAdminPanel bootstrapMode={bootstrapMode} />
      <WalletUnlockRequestsPanel />
      <ClientReleasesFeed />

      <section className="space-y-4">
        <SectionHeader icon={Banknote} title="Awaiting release" count={pending.length} live />
        {isLoading ? (
          <div className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : pending.length === 0 ? (
          <EmptyState message="No invoices awaiting release." />
        ) : (
          <div className="grid gap-3">
            {pending.map((inv) => (
              <ReleaseCard key={inv.id} inv={inv} onRelease={() => release.mutate(inv.id)} disabled={release.isPending} />
            ))}
          </div>
        )}
      </section>

      {released.length > 0 && (
        <section className="space-y-4">
          <SectionHeader icon={CheckCircle2} title="Recently released" count={released.length} />
          <DataTable>
            <thead>
              <tr>
                <th>Project</th>
                <th>Freelancer</th>
                <th className="text-end">Amount</th>
                <th>Released</th>
              </tr>
            </thead>
            <tbody>
              {released.map((inv) => (
                <tr key={inv.id}>
                  <td className="font-medium">{inv.projects?.title ?? "—"}</td>
                  <td>{inv.freelancer?.full_name ?? inv.freelancer?.email ?? "—"}</td>
                  <td className="text-end font-mono">${Number(inv.net_amount ?? inv.amount).toFixed(2)}</td>
                  <td className="text-muted-foreground">{inv.released_at ? new Date(inv.released_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </section>
      )}

      <WithdrawalsAdminPanel />
      <FreelancerWalletsPanel />
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  count,
  live,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count?: number;
  live?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="grid h-7 w-7 place-items-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <h2 className="text-sm font-semibold tracking-tight">
          {title}
          {count !== undefined && <span className="ms-1.5 text-muted-foreground">({count})</span>}
        </h2>
      </div>
      {live && (
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <TrendingUp className="h-3 w-3" /> Live
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-dashed py-10 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function DataTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card/60">
      <div className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <table className="admin-data-table w-full min-w-[640px] text-sm">
          {children}
        </table>
      </div>
    </div>
  );
}

function AdminOverview() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => {
      const [invRes, wRes, profRes, roleRes, devRes, payRes] = await Promise.all([
        supabase.from("invoices").select("amount, net_amount, status").in("status", ["paid_to_platform", "released_to_freelancer"]),
        supabase.from("withdrawals").select("amount, status"),
        supabase.from("profiles").select("id, role, kyc_status"),
        supabase.from("user_roles").select("user_id, role"),
        supabase.from("user_devices").select("user_id, is_active").eq("is_active", true),
        supabase.from("payout_profiles").select("user_id").eq("is_default", true),
      ]);
      if (invRes.error) throw invRes.error;
      if (wRes.error) throw wRes.error;
      if (profRes.error) throw profRes.error;
      if (roleRes.error) throw roleRes.error;

      const invoices = invRes.data ?? [];
      const withdrawals = wRes.data ?? [];
      const profiles = profRes.data ?? [];
      const roles = roleRes.data ?? [];
      const devices = devRes.data ?? [];
      const payouts = payRes.data ?? [];

      const inEscrow = invoices.filter((i) => i.status === "paid_to_platform").reduce((s, i) => s + Number(i.net_amount ?? i.amount ?? 0), 0);
      const released = invoices.filter((i) => i.status === "released_to_freelancer").reduce((s, i) => s + Number(i.net_amount ?? i.amount ?? 0), 0);
      const openWithdrawals = withdrawals.filter((w) => w.status === "pending" || w.status === "approved");
      const openWithdrawSum = openWithdrawals.reduce((s, w) => s + Number(w.amount || 0), 0);
      const paidWithdrawSum = withdrawals.filter((w) => w.status === "paid").reduce((s, w) => s + Number(w.amount || 0), 0);

      const freelancers = profiles.filter((p) => p.role === "freelancer");
      const freelancerIds = new Set(freelancers.map((f) => f.id));
      const verified = freelancers.filter((p) => p.kyc_status === "verified").length;
      const clients = profiles.filter((p) => p.role === "client").length;
      const admins = roles.filter((r) => r.role === "admin").length;
      const freelancerDeviceCount = devices.filter((d) => freelancerIds.has(d.user_id)).length;
      const uniqueDeviceFreelancers = new Set(devices.filter((d) => freelancerIds.has(d.user_id)).map((d) => d.user_id)).size;
      const payoutsConfigured = payouts.filter((p) => freelancerIds.has(p.user_id)).length;

      return {
        inEscrow,
        released,
        openWithdrawCount: openWithdrawals.length,
        openWithdrawSum,
        paidWithdrawSum,
        totalUsers: profiles.length,
        freelancers: freelancers.length,
        verifiedFreelancers: verified,
        clients,
        admins,
        freelancerDeviceCount,
        uniqueDeviceFreelancers,
        payoutsConfigured,
      };
    },
  });

  const fmt = (n: number) => `$${n.toFixed(2)}`;

  const inEscrowUp = useCountUp(data?.inEscrow ?? 0);
  const releasedUp = useCountUp(data?.released ?? 0);
  const paidOutUp = useCountUp(data?.paidWithdrawSum ?? 0);
  const openSumUp = useCountUp(data?.openWithdrawSum ?? 0);
  const usersUp = useCountUp(data?.totalUsers ?? 0);
  const verifiedUp = useCountUp(data?.verifiedFreelancers ?? 0);
  const devicesUp = useCountUp(data?.freelancerDeviceCount ?? 0);
  const payoutsUp = useCountUp(data?.payoutsConfigured ?? 0);

  const cards: Array<{
    label: string;
    display: string;
    hint: string;
    icon: typeof Landmark;
    tone: "amber" | "emerald" | "primary" | "sky" | "violet";
    pulse?: boolean;
  }> = [
    { label: "Protected", display: fmt(inEscrowUp), hint: "Client payments held on platform", icon: Landmark, tone: "amber", pulse: (data?.inEscrow ?? 0) > 0 },
    { label: "Released to freelancers", display: fmt(releasedUp), hint: "Lifetime net payouts", icon: Banknote, tone: "emerald" },
    { label: "Open withdrawals", display: `${data?.openWithdrawCount ?? 0} · ${fmt(openSumUp)}`, hint: "Pending + approved requests", icon: Wallet, tone: "primary", pulse: (data?.openWithdrawCount ?? 0) > 0 },
    { label: "Paid out", display: fmt(paidOutUp), hint: "Fiat withdrawals completed (crypto retired)", icon: BadgeCheck, tone: "emerald" },
    { label: "Users", display: String(Math.round(usersUp)), hint: data ? `${data.freelancers} freelancers · ${data.clients} clients · ${data.admins} admins` : "", icon: Users, tone: "sky" },
    { label: "Verified freelancers", display: data ? `${Math.round(verifiedUp)}/${data.freelancers}` : "—", hint: "KYC completed", icon: ShieldAlert, tone: "violet" },
    { label: "Payout destinations", display: data ? `${Math.round(payoutsUp)}/${data.freelancers}` : "—", hint: "Country-aware bank / BaridiMob configured", icon: Landmark, tone: "primary" },
    { label: "Freelancer devices", display: String(Math.round(devicesUp)), hint: data ? `${data.uniqueDeviceFreelancers} freelancers tracked · clients exempt` : "Hard-lock: 3 devices per freelancer", icon: Smartphone, tone: "sky" },
  ];

  const toneMap: Record<string, { text: string; ring: string; glow: string; bg: string }> = {
    amber: { text: "text-amber-500", ring: "ring-amber-500/20", glow: "hsl(38 92% 55% / 0.28)", bg: "bg-amber-500/10" },
    emerald: { text: "text-emerald-500", ring: "ring-emerald-500/20", glow: "hsl(158 84% 45% / 0.28)", bg: "bg-emerald-500/10" },
    primary: { text: "text-primary", ring: "ring-primary/20", glow: "hsl(var(--primary)/0.28)", bg: "bg-primary/10" },
    sky: { text: "text-sky-500", ring: "ring-sky-500/20", glow: "hsl(199 89% 55% / 0.28)", bg: "bg-sky-500/10" },
    violet: { text: "text-violet-500", ring: "ring-violet-500/20", glow: "hsl(268 84% 65% / 0.28)", bg: "bg-violet-500/10" },
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="grid h-7 w-7 place-items-center rounded-lg bg-primary/10 text-primary">
          <Activity className="h-3.5 w-3.5" />
        </div>
        <h2 className="text-sm font-semibold tracking-tight">Overview</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, idx) => {
          const Icon = c.icon;
          const t = toneMap[c.tone];
          return (
            <div
              key={c.label}
              className={cn(
                "group relative overflow-hidden rounded-xl border bg-card/60 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-card hover:shadow-lg ring-1",
                t.ring
              )}
              style={{ animation: `fadeInUp 0.5s ease-out ${idx * 70}ms both` }}
            >
              <span aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" style={{ background: t.glow }} />
              <div className="flex items-start justify-between">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{c.label}</span>
                <div className="flex items-center gap-2">
                  {c.pulse && (
                    <span className="relative flex h-2 w-2">
                      <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-70", t.text.replace("text-", "bg-"))} />
                      <span className={cn("relative inline-flex h-2 w-2 rounded-full", t.text.replace("text-", "bg-"))} />
                    </span>
                  )}
                  <div className={cn("inline-flex h-8 w-8 items-center justify-center rounded-lg", t.bg, t.text)}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
              </div>
              <div className={cn("mt-3 text-2xl font-semibold font-mono tabular-nums tracking-tight", t.text)}>{isLoading ? "…" : c.display}</div>
              {c.hint && <div className="mt-1 text-xs text-muted-foreground">{c.hint}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

interface UnlockRequestRow {
  id: string;
  freelancer_id: string;
  reason: string;
  proposed_address: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  admin_notes: string | null;
  created_at: string;
  resolved_at: string | null;
  freelancer: { full_name: string | null; email: string } | null;
}

function WalletUnlockRequestsPanel() {
  const qc = useQueryClient();
  const runResolve = useServerFn(resolveWalletUnlockRequest);

  const { data: requests, isLoading } = useQuery({
    queryKey: ["admin-wallet-unlock-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wallet_unlock_requests")
        .select("id, freelancer_id, reason, proposed_address, status, admin_notes, created_at, resolved_at")
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as Omit<UnlockRequestRow, "freelancer">[];
      const ids = Array.from(new Set(rows.map((r) => r.freelancer_id)));
      let byId = new Map<string, UnlockRequestRow["freelancer"]>();
      if (ids.length) {
        const { data: profs } = await supabase.from("profiles").select("id, full_name, email").in("id", ids);
        byId = new Map((profs ?? []).map((p) => [p.id, { full_name: p.full_name, email: p.email }]));
      }
      return rows.map((r) => ({ ...r, freelancer: byId.get(r.freelancer_id) ?? null })) as UnlockRequestRow[];
    },
  });

  const resolve = useMutation({
    mutationFn: (args: { id: string; action: "approve" | "reject"; notes?: string; minutes?: number }) =>
      runResolve({ data: { id: args.id, action: args.action, minutes: args.minutes ?? 30, admin_notes: args.notes } }),
    onSuccess: (_r, vars) => {
      toast.success(vars.action === "approve" ? "Wallet unlocked for 30 min" : "Request rejected");
      qc.invalidateQueries({ queryKey: ["admin-wallet-unlock-requests"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="space-y-4">
      <SectionHeader icon={Clock} title="Wallet unlock requests" count={requests?.length ?? 0} />
      {isLoading ? (
        <div className="rounded-xl border border-dashed py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : !requests || requests.length === 0 ? (
        <EmptyState message="No pending wallet unlock requests." />
      ) : (
        <div className="grid gap-3">
          {requests.map((r) => (
            <div key={r.id} className="rounded-xl border bg-card/60 p-4 space-y-3 transition-colors hover:bg-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{r.freelancer?.full_name ?? r.freelancer?.email ?? "Unknown"}</div>
                  <div className="text-xs text-muted-foreground">{r.freelancer?.email}</div>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-primary">Proposed new destination</div>
                <code className="mt-1 block truncate font-mono text-xs">{r.proposed_address}</code>
              </div>
              <div className="rounded-lg border bg-muted/40 p-3 text-xs">
                <span className="text-muted-foreground">Reason: </span>
                {r.reason}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const notes = window.prompt("Reason for rejection (optional):", "") ?? undefined;
                    resolve.mutate({ id: r.id, action: "reject", notes });
                  }}
                  disabled={resolve.isPending}
                >
                  Reject
                </Button>
                <Button size="sm" onClick={() => resolve.mutate({ id: r.id, action: "approve", minutes: 30 })} disabled={resolve.isPending}>
                  Approve (unlock 30 min)
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function ReleaseCard({ inv, onRelease, disabled }: { inv: AdminInvoice; onRelease: () => void; disabled: boolean }) {
  const gross = Number(inv.amount);
  const net = Number(inv.net_amount ?? inv.amount);
  const fee = Number(inv.stripe_fee_amount ?? Math.max(gross - net, 0));
  return (
    <div className="rounded-xl border bg-card/60 p-5 space-y-4 transition-colors hover:bg-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium">{inv.projects?.title ?? "—"}</div>
          <div className="text-xs text-muted-foreground">
            Client {inv.projects?.client_email} • paid {inv.paid_at ? new Date(inv.paid_at).toLocaleString() : "—"}
          </div>
        </div>
        <div className="text-end">
          <div className="text-lg font-semibold font-mono tracking-tight">${net.toFixed(2)}</div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">
            Client paid ${gross.toFixed(2)} · Stripe fee ${fee.toFixed(2)}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
        <div className="flex items-start gap-2">
          <Wallet className="mt-0.5 h-4 w-4 text-primary" />
          <div className="text-xs text-muted-foreground">
            Release <strong className="text-foreground">${net.toFixed(2)}</strong> to{" "}
            <span className="font-medium text-foreground">{inv.freelancer?.full_name ?? inv.freelancer?.email}</span>. Funds move from escrow to their available balance; they withdraw from Wallet.
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={onRelease} disabled={disabled}>
          Mark as released
        </Button>
      </div>
    </div>
  );
}

function GrantAdminPanel({ bootstrapMode }: { bootstrapMode: boolean }) {
  const [email, setEmail] = useState("");
  const qc = useQueryClient();
  const grant = useServerFn(grantAdminByEmail);
  const mut = useMutation({
    mutationFn: (e: string) => grant({ data: { email: e } }),
    onSuccess: (res) => {
      toast.success(`Admin granted to ${res.email}`);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["is-admin"] });
      qc.invalidateQueries({ queryKey: ["any-admin-exists"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <section className="rounded-xl border bg-card/60 p-5 space-y-3">
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-primary" />
        <h2 className="font-medium">Grant admin access</h2>
      </div>
      {bootstrapMode && <p className="text-xs text-amber-600 dark:text-amber-400">No admin exists yet — the first grant bootstraps the platform.</p>}
      <p className="text-xs text-muted-foreground">The person must already have signed up. Enter their account email.</p>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) mut.mutate(email.trim());
        }}
      >
        <Input type="email" placeholder="user@example.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={mut.isPending} />
        <Button type="submit" disabled={mut.isPending || !email.trim()}>
          Grant admin
        </Button>
      </form>
    </section>
  );
}

interface FreelancerWalletRow {
  id: string;
  full_name: string | null;
  email: string;
  kyc_status: string | null;
  payout_profile: {
    country: string;
    method: string;
    details: Record<string, string>;
  } | null;
}

function FreelancerWalletsPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-freelancer-wallets"],
    queryFn: async () => {
      const { data: profs, error } = await supabase.from("profiles").select("id, full_name, email, kyc_status").eq("role", "freelancer").order("created_at", { ascending: false });
      if (error) throw error;
      const ids = (profs ?? []).map((p) => p.id);
      const byId = new Map<string, FreelancerWalletRow["payout_profile"]>();
      if (ids.length) {
        const { data: pays } = await supabase.from("payout_profiles").select("user_id, country, method, details").in("user_id", ids).eq("is_default", true);
        (pays ?? []).forEach((p) =>
          byId.set(p.user_id, {
            country: p.country,
            method: p.method,
            details: (p.details ?? {}) as Record<string, string>,
          })
        );
      }
      return (profs ?? []).map((p) => ({
        id: p.id,
        full_name: p.full_name,
        email: p.email,
        kyc_status: p.kyc_status,
        payout_profile: byId.get(p.id) ?? null,
      })) as FreelancerWalletRow[];
    },
  });

  const rows = data ?? [];
  const withWallet = rows.filter((f) => f.payout_profile);
  const copy = (v: string) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied");
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionHeader icon={Landmark} title="Freelancer payout destinations" count={withWallet.length} />
        <span className="text-xs text-muted-foreground">{rows.length - withWallet.length} without destination</span>
      </div>
      {isLoading ? (
        <div className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState message="No freelancers yet." />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Freelancer</th>
              <th>Country</th>
              <th>Method</th>
              <th>Destination</th>
              <th>KYC</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const pp = f.payout_profile;
              const destination = pp ? resolveDestination(pp.method, pp.country, pp.details) : "";
              const methodLabel = pp ? formatMethodLabel(pp.method) : "—";
              return (
                <tr key={f.id} className="align-top">
                  <td>
                    <div className="font-medium">{f.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{f.email}</div>
                  </td>
                  <td className="whitespace-nowrap text-xs text-muted-foreground">{pp?.country ?? "—"}</td>
                  <td className="whitespace-nowrap">
                    {pp ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        <Landmark className="h-3 w-3" />
                        {methodLabel}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not set</span>
                    )}
                  </td>
                  <td>
                    {destination ? (
                      <code className="block max-w-[280px] break-all font-mono text-xs">{destination}</code>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="capitalize text-xs text-muted-foreground">{f.kyc_status ?? "—"}</td>
                  <td className="text-end">
                    {destination && (
                      <Button size="sm" variant="outline" onClick={() => copy(destination)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      )}
    </section>
  );
}
